/**
 * Admin API Module
 * Handles administrative operations
 * Part of API optimization - follows AIM atomicity principle
 */

import { api } from './client'

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  
  // User management
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  
  // Entity management
  getAllEntities: (params) => api.get('/admin/entities', { params }),
  getEntity: (id) => api.get(`/admin/entities/${id}`),
  createEntity: (data) => api.post('/admin/entities', data),
  updateEntity: (id, data) => api.put(`/admin/entities/${id}`, data),
  deleteEntity: (id) => api.delete(`/admin/entities/${id}`),
  verifyEntity: (id) => api.put(`/admin/entities/${id}/verify`),
  setFeaturedEntity: (id) => api.put(`/admin/entities/${id}/feature`),
  unsetFeaturedEntity: () => api.delete('/admin/entities/featured'),
  
  // Sighting management
  getAllSightings: (params) => api.get('/admin/sightings', { params }),
  getSighting: (id) => api.get(`/admin/sightings/${id}`),
  getSightingSourceBreakdown: () => api.get('/admin/sightings/sources'),
  createSighting: (data) => api.post('/admin/sightings', data),
  updateSighting: (id, data) => api.put(`/admin/sightings/${id}`, data),
  deleteSighting: (id) => api.delete(`/admin/sightings/${id}`),
  moderateSighting: (sightingId, action, reason) => api.post(`/admin/sightings/${sightingId}/moderate`, { action, reason }),
  
  // Image management
  moderateImage: (imageId, action, reason) => api.post(`/admin/images/${imageId}/moderate`, { action, reason }),
  setPrimaryImage: (imageId) => api.post(`/admin/images/${imageId}/set-primary`),
  deleteImage: (imageId) => api.delete(`/admin/images/${imageId}`),
  addImageToSighting: (sightingId, imageData) => api.post(`/admin/sightings/${sightingId}/images`, imageData),
  
  // Contact management
  getContacts: (params) => api.get('/admin/contacts', { params }),
  getContact: (id) => api.get(`/admin/contacts/${id}`),
  updateContactStatus: (id, data) => api.put(`/admin/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/admin/contacts/${id}`),

  // Notification management
  getDigestStats: () => api.get('/admin/notifications/digest/stats'),
  sendDigest: () => api.post('/admin/notifications/digest/send'),
  bulkUpdateDigest: (enabled) => api.patch('/admin/notifications/digest/bulk', { enabled }),
  getPushStats: () => api.get('/admin/notifications/push/stats'),
  broadcastPush: (data) => api.post('/admin/notifications/push/broadcast', data),

  // Review moderation
  getPendingReviews: (params) => api.get('/admin/reviews/pending', { params }),
  moderateReview: (id, action) => api.post(`/admin/reviews/${id}/moderate`, { action }),

  // IoT device token management
  listDevices: () => api.get('/admin/devices'),
  createDevice: (entityId, name) => api.post('/admin/devices', { entityId, name }),
  renameDevice: (id, name) => api.patch(`/admin/devices/${id}`, { name }),
  reissueToken: (id) => api.post(`/admin/devices/${id}/reissue`),
  revokeDevice: (id) => api.delete(`/admin/devices/${id}`),

  // City page management
  listCities: (params) => api.get('/admin/cities', { params }),
  getCity: (id) => api.get(`/admin/cities/${id}`),
  createCity: (data) => api.post('/admin/cities', data),
  updateCity: (id, data) => api.put(`/admin/cities/${id}`, data),
  deleteCity: (id) => api.delete(`/admin/cities/${id}`),

  // Posts management
  listPosts: (params) => api.get('/admin/posts', { params }),
  getPost: (id) => api.get(`/admin/posts/${id}`),
  createPost: (data) => api.post('/admin/posts', data),
  updatePost: (id, data) => api.put(`/admin/posts/${id}`, data),
  deletePost: (id) => api.delete(`/admin/posts/${id}`),
  publishPost: (id) => api.put(`/admin/posts/${id}/publish`),
  unpublishPost: (id) => api.put(`/admin/posts/${id}/unpublish`),
  pinPost: (id) => api.put(`/admin/posts/${id}/pin`),
  getPostTranslations: (id) => api.get(`/admin/posts/${id}/translations`),

  // Ephemeral listings admin
  getAdminListings: (params) => api.get('/admin/listings', { params }),
  getAdminListingRequests: (params) => api.get('/admin/listings/requests', { params }),
  adminFulfillListing: (id) => api.patch(`/admin/listings/${id}/fulfill`),
  adminDeleteListing: (id) => api.delete(`/admin/listings/${id}`),

  // Offerings / services admin
  getAdminOfferings: (params) => api.get('/admin/offerings', { params }),
  adminAddOffering: (entityId, data) => api.post(`/admin/entities/${entityId}/offerings`, data),
  adminUpdateOffering: (entityId, offeringId, data) => api.put(`/admin/entities/${entityId}/offerings/${offeringId}`, data),
  adminDeleteOffering: (entityId, offeringId) => api.delete(`/admin/entities/${entityId}/offerings/${offeringId}`),
}
